/**
 * Shot preview — shows current shot thumbnail with metadata pills.
 * For continuous mode, shows Shot A → Shot B side by side.
 * Tapping the thumbnail opens a fullscreen fly-in overlay with Swiper navigation.
 */

import { useState, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence } from 'motion/react';
import { ImageFlyOverlay } from '@/components/ImageFlyOverlay.tsx';
import { useProjectStore } from '@/store/projectStore.ts';
import { usePlaybackStore } from '@/store/playbackStore.ts';
import { useUiStore } from '@/store/uiStore.ts';
import type { TimelineShot } from '@/types/timeline.ts';
import styles from './AiWorkspace.module.css';

interface Props {
  shot: TimelineShot | null;
  /** For continuous mode — the second shot */
  toShot?: TimelineShot | null;
  isContinuous?: boolean;
}

/** Scroll the shot lane so the given time is visible */
function scrollLaneToTime(time: number) {
  const lane = document.querySelector('[data-orient="shot-lane"]')?.querySelector('[class*="lane"]') as HTMLElement | null;
  if (!lane) return;
  const pps = useUiStore.getState().pixelsPerSecond;
  const x = time * pps;
  const { clientWidth } = lane;
  lane.scrollLeft = Math.max(0, x - clientWidth / 2);
}

function ShotThumb({ shot }: { shot: TimelineShot }) {
  const { t } = useTranslation('aiWorkspace');
  const thumbRef = useRef<HTMLDivElement>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleClick = useCallback(() => {
    if (shot.thumbnailDataUrl) setPreviewOpen(true);
  }, [shot.thumbnailDataUrl]);

  // Build the items array from all project shots (so overlay always finds the target)
  const project = useProjectStore(s => s.project);
  const allShots = useMemo(() => {
    if (!project) return [];
    return project.scenes.flatMap(s => s.shots).filter(s => s.thumbnailDataUrl);
  }, [project]);
  const items = useMemo(() =>
    allShots.map(s => ({ url: s.thumbnailDataUrl!, videoUrl: s.videoObjectUrl })),
    [allShots],
  );

  // Find the active index based on the shot prop (not the timeline playhead)
  const activeIndex = allShots.findIndex(s => s.id === shot.id);

  const handleIndexChange = useCallback((newIndex: number) => {
    const targetShot = allShots[newIndex];
    if (!targetShot) return;
    const ps = useProjectStore.getState();
    const seek = usePlaybackStore.getState().seek;
    // Compute the start time for this shot
    const allProjectShots = ps.project?.scenes.flatMap(s => s.shots) ?? [];
    let cumulative = 0;
    for (const s of allProjectShots) {
      if (s.id === targetShot.id) break;
      cumulative += s.duration;
    }
    ps.selectShot(targetShot.id);
    seek(cumulative);
    scrollLaneToTime(cumulative);
  }, [allShots]);

  return (
    <>
      <div
        ref={thumbRef}
        className={styles.shotPreview}
        onClick={handleClick}
        style={{ cursor: shot.thumbnailDataUrl ? 'pointer' : undefined, position: 'relative' }}
      >
        {shot.thumbnailDataUrl ? (
          <img src={shot.thumbnailDataUrl} alt={t('shotAltText', { number: shot.number })} />
        ) : (
          <div className={styles.emptyState}>{t('noThumbnail')}</div>
        )}
        {shot.videoObjectUrl && (
          <div style={{
            position: 'absolute', top: 4, right: 4, width: 18, height: 18,
            background: 'rgba(0,0,0,0.6)', borderRadius: 4,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'none', color: '#fff',
          }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6.5 4.12v15.76a1 1 0 001.52.86l12.36-7.88a1 1 0 000-1.72L8.02 3.26a1 1 0 00-1.52.86z" />
            </svg>
          </div>
        )}
      </div>
      <AnimatePresence>
        {previewOpen && shot.thumbnailDataUrl && items.length > 0 && activeIndex >= 0 && (
          <ImageFlyOverlay
            key="image-fly-overlay"
            activeIndex={activeIndex}
            items={items}
            sourceElement={thumbRef.current}
            onClose={() => setPreviewOpen(false)}
            onIndexChange={handleIndexChange}
          />
        )}
      </AnimatePresence>
    </>
  );
}

export function WorkspaceShotPreview({ shot, toShot, isContinuous }: Props) {
  const { t } = useTranslation('aiWorkspace');
  if (!shot) {
    // The empty state is boxed in the same 16/9 .shotPreview frame a selected shot draws, matching
    // what Enhance already does for its own "tap a shot" states (StyleGradeTab :665, :674). A bare
    // text div collapsed the preview column, so Create/Touch Up/Import re-laid-out between "shot
    // selected" and "nothing selected" — and in portrait that flipped .controlsRow's wrap decision.
    return (
      <div className={styles.shotPreview}>
        <div className={styles.emptyState}>{t('noShotSelected')}</div>
      </div>
    );
  }

  if (isContinuous && toShot) {
    return (
      <div className={styles.continuousPreview}>
        <ShotThumb shot={shot} />
        <span className={styles.continuousArrow}>&rarr;</span>
        <ShotThumb shot={toShot} />
      </div>
    );
  }

  return <ShotThumb shot={shot} />;
}
